import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../components/AppIcon';
import {
  loginUser,
  initiateGoogleLogin,
  registerUser,
  resendVerificationEmail,
  verifyEmail,
  forgotPassword,
  resetPassword,
} from '../utils/api';
import { saveAuthToken, saveUserId } from '../utils/cookies';
import { useAuth } from './AuthContext';

const AuthModalContext = createContext(null);

const defaultOptions = {
  redirectTo: '/dashboard',
};

const AUTH_REDIRECT_STORAGE_KEY = 'prepxai_auth_redirect';
const PILOT_LOGIN_ONLY = false;

const normalizeAuthMode = (mode = 'login') => (
  PILOT_LOGIN_ONLY && mode !== 'login' ? 'login' : mode
);

const makeUsername = (email) => {
  const base = email
    .split('@')[0]
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, 18);
  const safeBase = base.length >= 3 ? base : 'user';
  return `${safeBase}${Math.floor(1000 + Math.random() * 9000)}`;
};

const GoogleLogo = () => (
  <svg
    aria-hidden="true"
    className="h-[18px] w-[18px]"
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.63-.06-1.24-.16-1.82H9v3.44h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.6Z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.47-.8 5.96-2.2l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.33-1.58-5.04-3.71H.95v2.34A9 9 0 0 0 9 18Z"
    />
    <path
      fill="#FBBC05"
      d="M3.96 10.69A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.16.28-1.69V4.97H.95A9 9 0 0 0 0 9c0 1.45.35 2.82.95 4.03l3.01-2.34Z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .95 4.97l3.01 2.34C4.67 5.18 6.66 3.58 9 3.58Z"
    />
  </svg>
);

const SocialButton = ({ label, onClick }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className="flex h-[46px] w-full items-center justify-center gap-2.5 rounded-[10px] border border-[#e0e1e6] bg-white text-[14px] font-bold text-[#303139] shadow-[0_2px_8px_rgba(15,23,42,0.04)] transition-all hover:-translate-y-0.5 hover:border-[#d1d3da] hover:shadow-[0_8px_18px_rgba(15,23,42,0.08)] active:translate-y-0 dark:border-[#1d1e26] dark:bg-[#17181e] dark:text-[#ECEFF4] dark:hover:border-emerald-700 dark:hover:bg-[#1a1c22]"
  >
    <GoogleLogo />
    <span>{label}</span>
  </button>
);

const OtpInput = ({ value, onChange }) => {
  const inputRefs = React.useRef([]);

  const digits = useMemo(() => {
    const val = value || '';
    return Array.from({ length: 6 }, (_, i) => val[i] || '');
  }, [value]);

  const handleChange = (e, index) => {
    const val = e.target.value;
    if (val && !/^\d+$/.test(val)) return;

    const digit = val.slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    const newValue = newDigits.join('');
    onChange(newValue);

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (!digits[index] && index > 0) {
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        onChange(newDigits.join(''));
        inputRefs.current[index - 1]?.focus();
      } else {
        const newDigits = [...digits];
        newDigits[index] = '';
        onChange(newDigits.join(''));
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasteData)) {
      onChange(pasteData);
      inputRefs.current[5]?.focus();
    }
  };

  return (
    <div className="flex justify-between gap-2 sm:gap-2.5" onPaste={handlePaste}>
      {Array.from({ length: 6 }).map((_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digits[index]}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          className="h-[44px] w-[44px] sm:h-[48px] sm:w-[48px] rounded-[10px] border border-[#dedee3] bg-white text-center text-[18px] font-bold text-slate-900 outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 dark:border-[#1d1e26] dark:bg-[#08080a] dark:text-[#ECEFF4] dark:focus:border-emerald-600 dark:focus:ring-emerald-600/20"
        />
      ))}
    </div>
  );
};

const AuthModal = ({ state, close, setMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const isRegister = !PILOT_LOGIN_ONLY && state.mode === 'register';
  const isForgot = !PILOT_LOGIN_ONLY && state.mode === 'forgot-password';
  const isReset = !PILOT_LOGIN_ONLY && state.mode === 'reset-password';
  const redirectTo = state.options?.redirectTo || defaultOptions.redirectTo;

  const [email, setEmail] = useState('');
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  // const [aiAccepted, setAiAccepted] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpUser, setOtpUser] = useState(null);
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  useEffect(() => {
    if (!state.isOpen) return;
    setError('');
    setNotice('');
    setPassword('');
    setOtp('');
    setShowOtp(false);
    setOtpUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setShow2FA(false);
    setTwoFactorCode('');
    if (state.options?.email) {
      setEmail(state.options.email);
    }
  }, [state.isOpen, state.mode, state.options]);

  useEffect(() => {
    if (!state.isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [state.isOpen]);

  if (!state.isOpen) return null;

  const handleClose = () => {
    close();
    if (location.pathname === '/login' || location.pathname === '/register') {
      navigate('/', { replace: true });
    }
  };

  const validateSignup = () => {
    if (!policyAccepted) {
      setError('Please accept prepxai policies to continue.');
      return false;
    }
    // if (!aiAccepted) {
    //   setError('Please acknowledge that AI-generated evaluations may contain inaccuracies.');
    //   return false;
    // }
    return true;
  };

  const completeAuth = async (user, token) => {
    if (user?.id) saveUserId(user.id);
    if (token) saveAuthToken(token);
    await checkAuth();
    close();
    if (user?.onboarded) {
      navigate(redirectTo);
    } else {
      navigate('/onboarding');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);

    try {
      if (isForgot) {
        if (!email) throw new Error('Email is required.');
        const response = await forgotPassword(email);
        setNotice(response.message || 'Reset code sent successfully.');
        setTimeout(() => {
          setMode('reset-password', { email });
        }, 2000);
        return;
      }

      if (isReset) {
        if (!email) throw new Error('Email is required.');
        if (!otp) throw new Error('Reset code is required.');
        if (!newPassword) throw new Error('New password is required.');
        if (newPassword !== confirmPassword) throw new Error('Passwords do not match.');

        const response = await resetPassword({
          email,
          otp,
          newPassword,
          twoFactorCode: show2FA ? twoFactorCode : undefined,
        });

        setNotice(response.message || 'Password reset successfully.');
        setTimeout(() => {
          setMode('login');
        }, 2000);
        return;
      }

      if (isRegister) {
        if (!email) throw new Error('Email is required.');
        if (!password) throw new Error('Password is required.');
        if (password.length < 6) throw new Error('Password must be at least 6 characters long.');
        if (!validateSignup()) return;

        const response = await registerUser({
          email,
          username: makeUsername(email),
          password,
        });

        if (response.user?.id) saveUserId(response.user.id);
        if (response.token) saveAuthToken(response.token);
        setOtpUser(response.user);
        setShowOtp(true);
        setNotice(`We sent a 6-digit verification code to ${email}.`);
        return;
      }

      if (!emailOrUsername) throw new Error('Email or username is required.');
      if (!password) throw new Error('Password is required.');

      const response = await loginUser({ emailOrUsername, password });
      await completeAuth(response.user, response.token);
    } catch (err) {
      if (isReset && err.error === '2fa_required') {
        setShow2FA(true);
        setError('Two-Factor Authentication is active on your account. Please enter your 6-digit Authenticator OTP code below.');
      } else {
        setError(err.error || err.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    if (PILOT_LOGIN_ONLY) return;
    setError('');
    setLoading(true);

    try {
      if (!otp || otp.length < 6) {
        throw new Error('Please enter a valid 6-digit code.');
      }
      await verifyEmail({ otp });
      await checkAuth();
      close();
      navigate(otpUser?.onboarded ? redirectTo : '/onboarding');
    } catch (err) {
      setError(err.error || err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    setError('');
    if (isRegister && !validateSignup()) return;
    sessionStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, redirectTo || '/interview-setup');
    initiateGoogleLogin();
  };

  const handleResend = async () => {
    setError('');
    setNotice('');
    try {
      await resendVerificationEmail();
      setNotice('Verification code resent. Please check your email.');
    } catch (err) {
      setError(err.error || err.message || 'Failed to resend code.');
    }
  };

  const switchMode = (nextMode) => {
    setMode(normalizeAuthMode(nextMode), state.options);
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/55 px-4 py-6 backdrop-blur-[2px] dark:bg-black/70">
      <button className="absolute inset-0 cursor-default" type="button" aria-label="Close authentication modal" onClick={handleClose} />

      <div className="relative w-full max-w-[28rem] overflow-hidden rounded-[14px] border border-white/70 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.30)] dark:border-[#1d1e26] dark:bg-[#111216] dark:shadow-none">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-[#ECEFF4]"
          aria-label="Close"
        >
          <Icon name="X" size={18} />
        </button>

        <div className="max-h-[88vh] overflow-y-auto px-5 pb-6 pt-9 sm:px-7">
          <div className="mb-5 text-center">
            <div className="mb-4 flex items-center justify-center">
              <span className="text-xl font-normal tracking-tight text-slate-900 dark:text-[#ECEFF4]">
                prepx<span className="text-emerald-600 dark:text-[#10E58F]">ai</span>
              </span>
            </div>

            <h2 className="text-[21px] font-semibold tracking-tight text-[#292a30] dark:text-[#ECEFF4]">
              {showOtp ? 'Check your email' : isForgot || isReset ? 'Reset Password' : isRegister ? 'Create your free account' : 'Welcome back'}
            </h2>
            <p className="mx-auto mt-2 max-w-[22rem] text-[12.5px] font-medium leading-[18px] text-[#8a8c98] dark:text-[#94A3B8]">
              {showOtp
                ? `Enter the verification code sent to ${email}.`
                : isForgot
                  ? 'Enter your email address to receive a password reset code.'
                  : isReset
                    ? 'Enter the 6-digit code sent to your email and set your new password.'
                    : isRegister
                      ? 'Land your next job faster. Practice interviews, get confident, get hired - all in one platform'
                      : 'Sign in to continue your interview prep, reports, and practice history'}
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-[10px] border border-red-200 bg-red-50 px-3.5 py-2.5 text-xs font-medium text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
              {error}
            </div>
          )}

          {notice && (
            <div className="mb-4 rounded-[10px] border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-xs font-medium text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
              {notice}
            </div>
          )}

          {showOtp ? (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-[13px] font-semibold text-[#303139] dark:text-[#ECEFF4]">Verification code</label>
                <OtpInput
                  value={otp}
                  onChange={(val) => {
                    setOtp(val);
                    if (error) setError('');
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="auth-modal-primary flex h-[46px] w-full items-center justify-center rounded-[8px] bg-emerald-600 text-[15px] font-bold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-emerald-600 dark:text-white dark:hover:bg-[#10E58F]"
              >
                {loading ? 'Verifying...' : 'Verify email'}
              </button>

              <button
                type="button"
                onClick={handleResend}
                className="w-full text-center text-sm font-semibold text-emerald-700 hover:underline dark:text-[#10E58F]"
              >
                Resend code
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                {isForgot ? (
                  <div>
                    <label className="mb-1.5 block text-[13px] font-semibold text-[#303139] dark:text-[#ECEFF4]">
                      Email address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        if (error) setError('');
                      }}
                      className="h-[46px] w-full rounded-[10px] border border-[#dedee3] bg-white px-3.5 text-[14px] font-medium text-slate-900 outline-none transition placeholder:text-[#9a9ca8] focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 dark:border-[#1d1e26] dark:bg-[#08080a] dark:text-[#ECEFF4] dark:placeholder:text-[#64748B] dark:focus:border-emerald-600 dark:focus:ring-emerald-600/20"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>
                ) : isReset ? (
                  <>
                    <div>
                      <label className="mb-1.5 block text-[13px] font-semibold text-[#303139] dark:text-[#ECEFF4]">
                        Email address
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          if (error) setError('');
                        }}
                        className="h-[46px] w-full rounded-[10px] border border-[#dedee3] bg-[#f5f5f5] px-3.5 text-[14px] font-medium text-slate-500 outline-none transition placeholder:text-[#9a9ca8] dark:border-[#1d1e26] dark:bg-[#111216] dark:text-[#64748B]"
                        placeholder="Enter your email address"
                        required
                        disabled
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-[13px] font-semibold text-[#303139] dark:text-[#ECEFF4]">
                        Reset Code
                      </label>
                      <OtpInput
                        value={otp}
                        onChange={(val) => {
                          setOtp(val);
                          if (error) setError('');
                        }}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-[13px] font-semibold text-[#303139] dark:text-[#ECEFF4]">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(event) => {
                            setNewPassword(event.target.value);
                            if (error) setError('');
                          }}
                          className="h-[46px] w-full rounded-[10px] border border-[#dedee3] bg-white px-3.5 pr-12 text-[14px] font-medium text-slate-900 outline-none transition placeholder:text-[#9a9ca8] focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 dark:border-[#1d1e26] dark:bg-[#08080a] dark:text-[#ECEFF4] dark:placeholder:text-[#64748B] dark:focus:border-emerald-600 dark:focus:ring-emerald-600/20"
                          placeholder="At least 6 characters"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((value) => !value)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999ba3] transition hover:text-slate-700 dark:text-[#64748B] dark:hover:text-[#ECEFF4]"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-[13px] font-semibold text-[#303139] dark:text-[#ECEFF4]">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(event) => {
                          setConfirmPassword(event.target.value);
                          if (error) setError('');
                        }}
                        className="h-[46px] w-full rounded-[10px] border border-[#dedee3] bg-white px-3.5 text-[14px] font-medium text-slate-900 outline-none transition placeholder:text-[#9a9ca8] focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 dark:border-[#1d1e26] dark:bg-[#08080a] dark:text-[#ECEFF4] dark:placeholder:text-[#64748B] dark:focus:border-emerald-600 dark:focus:ring-emerald-600/20"
                        placeholder="Confirm your password"
                        required
                      />
                    </div>

                    {show2FA && (
                      <div>
                        <label className="mb-1.5 block text-[13px] font-semibold text-[#303139] dark:text-[#ECEFF4]">
                          2FA Authenticator Code
                        </label>
                        <input
                          type="text"
                          value={twoFactorCode}
                          onChange={(event) => {
                            setTwoFactorCode(event.target.value.replace(/\D/g, ''));
                            if (error) setError('');
                          }}
                          maxLength={6}
                          className="h-[46px] w-full rounded-[10px] border border-[#dedee3] bg-white px-3.5 text-[14px] font-medium text-slate-900 outline-none transition placeholder:text-[#9a9ca8] focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 dark:border-[#1d1e26] dark:bg-[#08080a] dark:text-[#ECEFF4] dark:placeholder:text-[#64748B] dark:focus:border-emerald-600 dark:focus:ring-emerald-600/20"
                          placeholder="Enter 6-digit code"
                          required
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <label className="mb-1.5 block text-[13px] font-semibold text-[#303139] dark:text-[#ECEFF4]">
                        {isRegister ? 'Email address' : 'Email address or username'}
                      </label>
                      <input
                        type={isRegister ? 'email' : 'text'}
                        value={isRegister ? email : emailOrUsername}
                        onChange={(event) => {
                          if (isRegister) setEmail(event.target.value);
                          else setEmailOrUsername(event.target.value);
                          if (error) setError('');
                        }}
                        className="h-[46px] w-full rounded-[10px] border border-[#dedee3] bg-white px-3.5 text-[14px] font-medium text-slate-900 outline-none transition placeholder:text-[#9a9ca8] focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 dark:border-[#1d1e26] dark:bg-[#08080a] dark:text-[#ECEFF4] dark:placeholder:text-[#64748B] dark:focus:border-emerald-600 dark:focus:ring-emerald-600/20"
                        placeholder={isRegister ? 'Enter your email address' : 'Enter your email or username'}
                        required
                      />
                    </div>

                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <label className="block text-[13px] font-semibold text-[#303139] dark:text-[#ECEFF4]">Password</label>
                        {!PILOT_LOGIN_ONLY && !isRegister && (
                          <button
                            type="button"
                            onClick={() => switchMode('forgot-password')}
                            className="text-xs font-semibold text-emerald-700 hover:underline dark:text-[#10E58F]"
                          >
                            Forgot?
                          </button>
                        )}
                      </div>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(event) => {
                            setPassword(event.target.value);
                            if (error) setError('');
                          }}
                          className="h-[46px] w-full rounded-[10px] border border-[#dedee3] bg-white px-3.5 pr-12 text-[14px] font-medium text-slate-900 outline-none transition placeholder:text-[#9a9ca8] focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 dark:border-[#1d1e26] dark:bg-[#08080a] dark:text-[#ECEFF4] dark:placeholder:text-[#64748B] dark:focus:border-emerald-600 dark:focus:ring-emerald-600/20"
                          placeholder="Enter your password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((value) => !value)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999ba3] transition hover:text-slate-700 dark:text-[#64748B] dark:hover:text-[#ECEFF4]"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                        </button>
                      </div>
                    </div>

                    {isRegister && (
                      <div className="space-y-2.5 rounded-[10px] border border-[#ededf0] bg-[#fafafa] p-3 dark:border-[#1d1e26] dark:bg-[#17181e]">
                        <label className="flex items-start gap-2.5 text-[11.5px] font-medium leading-[18px] text-[#71737e] dark:text-[#94A3B8]">
                          <input
                            type="checkbox"
                            checked={policyAccepted}
                            onChange={(event) => {
                              setPolicyAccepted(event.target.checked);
                              if (error) setError('');
                            }}
                            className="mt-0.5 h-4 w-4 rounded border-[#d7d7dd] text-emerald-600 focus:ring-emerald-600 dark:border-[#2a2d36] dark:bg-[#08080a]"
                          />
                          <span>
                            By signing up, you agree to prepxai&apos;s{' '}
                            <a href="/terms" className="font-semibold text-emerald-700 underline underline-offset-2 transition-colors hover:text-emerald-600 dark:text-[#10E58F] dark:hover:text-emerald-300">Terms</a>,{' '}
                            <a href="/privacy" className="font-semibold text-emerald-700 underline underline-offset-2 transition-colors hover:text-emerald-600 dark:text-[#10E58F] dark:hover:text-emerald-300">Privacy Policy</a>,{' '}
                            <a href="/refund" className="font-semibold text-emerald-700 underline underline-offset-2 transition-colors hover:text-emerald-600 dark:text-[#10E58F] dark:hover:text-emerald-300">Refund Policy</a>, and related policies.
                          </span>
                        </label>
                      </div>
                    )}
                  </>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="auth-modal-primary flex h-[46px] w-full items-center justify-center rounded-[8px] bg-emerald-600 text-[15px] font-bold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-emerald-600 dark:text-white dark:hover:bg-[#10E58F]"
                >
                  {loading ? 'Please wait...' : isForgot ? 'Send Reset Code' : isReset ? 'Reset Password' : 'Continue'}
                </button>
              </form>

              {!PILOT_LOGIN_ONLY && !isForgot && !isReset && (
                <>
                  <div className="my-5 flex items-center gap-3">
                    <div className="h-px flex-1 bg-[#ececf0] dark:bg-[#1d1e26]" />
                    <span className="text-[13px] font-semibold text-[#8f929c] dark:text-[#94A3B8]">or</span>
                    <div className="h-px flex-1 bg-[#ececf0] dark:bg-[#1d1e26]" />
                  </div>

                  <SocialButton
                    label={isRegister ? 'Create account with Google' : 'Login with Google'}
                    onClick={handleGoogle}
                  />
                </>
              )}

              <p className="mt-5 text-center text-[13px] font-medium text-[#8a8c98] dark:text-[#94A3B8]">
                {isForgot || isReset ? (
                  <>
                    Remembered your password?{' '}
                    <button
                      type="button"
                      onClick={() => switchMode('login')}
                      className="font-semibold text-emerald-700 underline underline-offset-2 transition-colors hover:text-emerald-600 dark:text-[#10E58F] dark:hover:text-emerald-300"
                    >
                      Sign in
                    </button>
                  </>
                ) : PILOT_LOGIN_ONLY ? (
                  <span>Pilot access is limited to assigned test accounts.</span>
                ) : (
                  <>
                    {isRegister ? 'Already have an account?' : 'New to prepxai?'}{' '}
                    <button
                      type="button"
                      onClick={() => switchMode(isRegister ? 'login' : 'register')}
                      className="font-semibold text-emerald-700 underline underline-offset-2 transition-colors hover:text-emerald-600 dark:text-[#10E58F] dark:hover:text-emerald-300"
                    >
                      {isRegister ? 'Sign in' : 'Create account'}
                    </button>
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const AuthModalProvider = ({ children }) => {
  const [state, setState] = useState({
    isOpen: false,
    mode: 'login',
    options: defaultOptions,
  });

  const openAuthModal = useCallback((mode = 'login', options = {}) => {
    setState({
      isOpen: true,
      mode: normalizeAuthMode(mode),
      options: { ...defaultOptions, ...options },
    });
  }, []);

  const closeAuthModal = useCallback(() => {
    setState((current) => ({ ...current, isOpen: false }));
  }, []);

  const setMode = useCallback((mode, options = null) => {
    setState((current) => ({
      ...current,
      isOpen: true,
      mode: normalizeAuthMode(mode),
      options: { ...defaultOptions, ...(options || current.options) },
    }));
  }, []);

  useEffect(() => {
    const handleOpenAuthModal = (event) => {
      openAuthModal(event.detail?.mode || 'login', event.detail?.options || {});
    };
    window.addEventListener('prepxai:open-auth-modal', handleOpenAuthModal);
    return () => window.removeEventListener('prepxai:open-auth-modal', handleOpenAuthModal);
  }, [openAuthModal]);

  const value = useMemo(
    () => ({ openAuthModal, closeAuthModal, setMode, authModalState: state }),
    [closeAuthModal, openAuthModal, setMode, state],
  );

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <AuthModal state={state} close={closeAuthModal} setMode={setMode} />
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = () => {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within AuthModalProvider');
  }
  return context;
};
